import { Injectable } from '@angular/core';

/**
 * SafeLocalStorageService provides a defensive wrapper around localStorage access
 * to prevent errors when localStorage is null, unavailable, or quota exceeded.
 * 
 * This addresses the issue where localStorage can be:
 * - null in private/incognito browsing mode
 * - disabled by browser security settings
 * - inaccessible due to quota exceeded
 * - blocked in cross-origin/sandboxed iframe contexts
 */
@Injectable({
  providedIn: 'root'
})
export class SafeLocalStorageService {
  private isAvailable: boolean;
  private hasLoggedWarning = false;

  constructor() {
    this.isAvailable = this.checkAvailability();
  }

  /**
   * Checks if localStorage is available and accessible
   */
  private checkAvailability(): boolean {
    try {
      if (typeof window === 'undefined' || typeof localStorage === 'undefined' || localStorage === null) {
        return false;
      }
      
      // Test actual read/write access
      const testKey = '__ls_test__';
      localStorage.setItem(testKey, 'test');
      localStorage.removeItem(testKey);
      return true;
    } catch (e) {
      // Handle QuotaExceededError, SecurityError, or any other localStorage errors
      if (!this.hasLoggedWarning) {
        console.warn('localStorage is not available:', e);
        this.hasLoggedWarning = true;
      }
      return false;
    }
  }

  /**
   * Safely gets an item from localStorage
   * @param key - The key to retrieve
   * @param defaultValue - Optional default value if key doesn't exist or localStorage is unavailable
   * @returns The value from localStorage, or defaultValue, or null
   */
  getItem(key: string, defaultValue: string | null = null): string | null {
    if (!this.isAvailable) {
      return defaultValue;
    }

    try {
      const value = localStorage.getItem(key);
      return value !== null ? value : defaultValue;
    } catch (e) {
      console.error(`Error accessing localStorage.getItem('${key}'):`, e);
      // Re-check availability in case localStorage became unavailable
      this.isAvailable = this.checkAvailability();
      return defaultValue;
    }
  }

  /**
   * Safely sets an item in localStorage
   * @param key - The key to set
   * @param value - The value to store
   * @returns true if successful, false otherwise
   */
  setItem(key: string, value: string): boolean {
    if (!this.isAvailable) {
      console.warn(`Cannot set localStorage item '${key}': localStorage is not available`);
      return false;
    }

    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.error(`Error setting localStorage.setItem('${key}'):`, e);
      
      // Check if it's a quota exceeded error
      if (e instanceof DOMException && (
        e.name === 'QuotaExceededError' ||
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED'
      )) {
        console.error('localStorage quota exceeded. Consider clearing old data.');
      }
      
      // Re-check availability
      this.isAvailable = this.checkAvailability();
      return false;
    }
  }

  /**
   * Safely removes an item from localStorage
   * @param key - The key to remove
   * @returns true if successful, false otherwise
   */
  removeItem(key: string): boolean {
    if (!this.isAvailable) {
      return false;
    }

    try {
      localStorage.removeItem(key);
      return true;
    } catch (e) {
      console.error(`Error removing localStorage item '${key}':`, e);
      this.isAvailable = this.checkAvailability();
      return false;
    }
  }

  /**
   * Safely clears all localStorage
   * @returns true if successful, false otherwise
   */
  clear(): boolean {
    if (!this.isAvailable) {
      return false;
    }

    try {
      localStorage.clear();
      return true;
    } catch (e) {
      console.error('Error clearing localStorage:', e);
      this.isAvailable = this.checkAvailability();
      return false;
    }
  }

  /**
   * Checks if localStorage is currently available
   * @returns true if localStorage is accessible, false otherwise
   */
  isLocalStorageAvailable(): boolean {
    return this.isAvailable;
  }

  /**
   * Force re-check of localStorage availability
   * Useful after detecting potential storage issues
   */
  recheckAvailability(): boolean {
    this.isAvailable = this.checkAvailability();
    return this.isAvailable;
  }
}